import type { ReactNode } from 'react';

export type AttentionSeverity = 'legal-block' | 'deadline' | 'integrity' | 'warn' | 'routine';

export type AttentionItem = {
  id: string;
  /** Area-owned consequence or urgency, never volume alone. */
  protected?: boolean;
  limited?: boolean;
  volume?: boolean;
  sentence?: string;
  severity: AttentionSeverity;
  count: number | null;
  title: string;
  reason: string;
  unavailableReason?: 'failed' | 'unsupported' | 'loading' | 'limited';
  unavailableContext?: 'scope' | 'view';
  chips?: string[];
  group?: { id: string; title: string; label: string };
  actionPriority?: number;
  urgencyPriority?: number;
  detail?: ReactNode;
  breakdown?: AttentionItem[];
  action: { label: string; href?: string; control?: ReactNode };
};

export const isAttentionLimited = (item: AttentionItem): boolean =>
  Boolean(item.limited || item.unavailableReason === 'limited');

const severityOrder: Record<AttentionSeverity, number> = {
  'legal-block': 0,
  warn: 1,
  deadline: 2,
  integrity: 3,
  routine: 4,
};

export const compareAttention = (a: AttentionItem, b: AttentionItem): number =>
  Number(Boolean(b.protected)) - Number(Boolean(a.protected)) ||
  severityOrder[a.severity] - severityOrder[b.severity] ||
  (a.urgencyPriority ?? 10) - (b.urgencyPriority ?? 10) ||
  (a.actionPriority ?? 10) - (b.actionPriority ?? 10);

/** Group known outstanding checks only. Counts may overlap, so keep the lead count. */
export function groupAttentionItems(items: AttentionItem[]): AttentionItem[] {
  const groups = new Map<string, AttentionItem[]>();
  for (const item of items.filter((item) => item.count !== null && item.count > 0)) {
    const key = item.protected ? item.id : (item.group?.id ?? item.id);
    const members = groups.get(key) ?? [];
    members.push(item);
    groups.set(key, members);
  }
  return [...groups.values()]
    .flatMap((members) => {
      members.sort(compareAttention);
      const lead = members[0];
      if (!lead) return [];
      if (!lead.group || members.length === 1) return [lead];
      return [
        {
          ...lead,
          id: lead.group.id,
          limited: members.some(isAttentionLimited),
          title: lead.group.title,
          reason: members
            .slice(0, 3)
            .map(
              (item) =>
                `${item.group?.label ?? item.title} ${isAttentionLimited(item) ? 'at least ' : ''}${item.count}`,
            )
            .join(' · '),
          chips: [...new Set(members.flatMap((item) => item.chips ?? []))].slice(0, 3),
          breakdown: members,
        },
      ];
    })
    .sort(compareAttention);
}

export function attentionCountLabel(item: AttentionItem): string | number {
  if (item.count !== null) return isAttentionLimited(item) ? `At least ${item.count}` : item.count;
  if (item.unavailableReason === 'limited') return '— · limited';
  if (item.unavailableReason === 'unsupported')
    return `— · count not available in this ${item.unavailableContext ?? 'scope'}`;
  return item.unavailableReason === 'loading' ? '— · Checking…' : '— · couldn’t load';
}
